import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import QuickItemViewBox from "../../components/DataDisplayer/QuickItemViewBox";
import { ISelectFieldSingleItem } from "../../components/Form/DynamicForm";
import { IAxiosDefaultDataObject } from "../../config";
import api from "../../services/api";
import BaseApp, { IBaseApp, IBaseAppDataDisplayerConfig, IBaseAppFormConfig, ISearchParam } from "../BaseApp";
import ItemsApp from "../items/ItemsApp";
import { addNewItem, useReturnAllItems } from "../items/itemsSlice";
import CreateNewIssuePage from "./CreateNewIssuePage";
import { addNewIssue, updateIssue } from "./issuesSlice";
import { addNewUser, useReturnAllUsers } from "../user/userSlice";
import UsersApp from "../user/UsersApp";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import { ICellRendererParams } from "ag-grid-community";
import { IAttachment } from "../../components/Attachments";

/**
 * Issue Model
 */
export interface IIssuesModal{
    id: number,
    status: string,
    item_id: number,
    reported_by: number,
    issue_type: string,
    description: string,
    timestamp: string,
    resolved: boolean,
    resolved_at: string,
    resolved_by: number,
    resolved_description: string,
    reported_by_title: string,
    resolved_by_title: string,
    item_title: string,
    quantity: number,
    request_ref: string | null,
    warehouse_id: number | null,
    section_id: number | null,
    location_id: null | null,
    warehouse: string,
    section: string,
    location: string,
    caused_by_name: string,
    caused_by: number | null,
    description_text: string,
    resolved_text: string,
    issue_images: IAttachment[],
    resolved_images: IAttachment[],
}

export const EmptyIssueModel: IIssuesModal = {
    id: 0,
    item_id: 0,
    status: "",
    reported_by: 0,
    issue_type: "",
    description: "",
    timestamp: "",
    resolved: false,
    resolved_at: "",
    resolved_by: 0,
    resolved_description: "",
    reported_by_title: "",
    resolved_by_title: "",
    item_title: "",
    quantity: 0,
    request_ref: "",
    warehouse_id: null,
    section_id: null,
    location_id: null,
    warehouse: "",
    section: "",
    location: "",
    caused_by_name: "",
    caused_by: 0,
    description_text: "",
    resolved_text: "",
    issue_images: [],
    resolved_images: []
}

export const issuesTypesList: Array<ISelectFieldSingleItem> = [
    {
        name: "Missing",
        value: "missing"
    },
    {
        name: "Damaged",
        value: "damaged"
    },
]

interface IIssuesApp extends IBaseApp {
    listOfInstancesForItem: (itemId: number, page: number, search: string|null,  disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => Promise<any>,
    resolveIssue: (issueId: number, data: IAxiosDefaultDataObject) => Promise<any>,
}

/**
 * Issues App
 */
export default class IssuesApp extends BaseApp implements IIssuesApp {

    appName = "issues"
    appBaseEndPoint = "/items/issues"

    customListEndPoint: string = this.appBaseEndPoint

    elemPrefix: string = "issues_"

    /**
     * Pages
     */
    redux = {
        updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(updateIssue((item as IIssuesModal))),
        addNewItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(addNewIssue((item as IIssuesModal)))
    }

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "status",
                type: "status",
                label: "Status",
                maxWidth: 150,
                minWidth: 150,
            },
            {
                id: "issue_type",
                type: "status",
                label: "Type",
                maxWidth: 150,
                minWidth: 150,
            },
            {
                id: "item_title",
                type: "text",
                label: "Item",
                maxWidth: 200,
                minWidth: 200,
                quickItemView : (item, shown, handleHide) => <QuickItemViewBox shown={shown} handleHide={handleHide} itemsList={useReturnAllItems} itemId={item.item_id} baseApp={new ItemsApp()} />
            },
            {
                id: "reported_by_title",
                type: "text",
                label: "Reported by",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "timestamp",
                type: "date",
                label: "Reported on",
                maxWidth: 250,
                minWidth: 250,
            },
            {
                id: "resolved_by_title",
                type: "text",
                label: "Resolved by",
                maxWidth: 200,
                minWidth: 200,
            },
            {
                id: "resolved_at",
                type: "date",
                label: "Resolved on",
                maxWidth: 250,
                minWidth: 250,
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No issues have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "issues_form",
            elements: [
                {
                    id: this.elemPrefix + "item_id",
                    xs: 6,
                    // type: "DynamicSelect",
                    type: "DynamicModelSelect",
                    label: "Item",
                    fullWidth: true,
                    appClass: new ItemsApp(),
                    selectLabel: "Select Item",
                    gridColumns: new ItemsApp().gridDataColumns
                },
                {
                    id: this.elemPrefix + "issue_type",
                    xs: 6,
                    type: "Select",
                    label: "Type",
                    fullWidth: true,
                    items: issuesTypesList
                },
                {
                    id: this.elemPrefix + "description",
                    xs: 12,
                    type: "BlockEditor",
                    label: "Description",
                    fullWidth: true,
                },
                {
                    id: this.elemPrefix + "reported_by",
                    xs: 6,
                    type: "DynamicModelSelect",
                    label: "User",
                    fullWidth: true,
                    appClass: new UsersApp(),
                    selectLabel: "Select User",
                    gridColumns: new UsersApp().gridDataColumns
                },
                {
                    id: this.elemPrefix + "timestamp",
                    xs: 6,
                    type: "DateAndTime",
                    label: "Timestamp",
                    fullWidth: true,
                    // defaultValue: new Date()
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the issue details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "issue_type",
                message: "Issue type is required!"
            },
            {
                id: this.elemPrefix + "reported_by",
                message: "Reported by is required!"
            },
            {
                id: this.elemPrefix + "timestamp",
                message: "Timestamp by is required!"
            },
        ]
    }

    /**
     * List of instances
     */
    listOfInstancesForItem = async(itemId: number, page: number, search: string | null, disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => {

        let path: string = this.appBaseEndPoint + "/"

        if (this.customListEndPoint){
            path = this.customListEndPoint
        }

        path += `?page=${page ? page : 1}&item_id=${itemId}`

        if (search !== null){
            path += "&search=" + search
        }

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    /**
     * Resolve issue
     */
    resolveIssue = async(issueId: number, data: IAxiosDefaultDataObject) => {
        const response = await api.post(this.appBaseEndPoint + "/" + issueId + "/resolve", data)
        return response
    }

    gridDataColumns: GridDataColumns = [
                            {
                                field: "status",
                                headerName: "Status",
                                type: "status"
                            },
                            {
                                field: "issue_type",
                                headerName: "Type",
                                type: "status"
                            },
                            {
                                field: "item_title",
                                headerName: "Item",
                                // cellRenderer: (params: ICellRendererParams<IIssuesModal>) => {
                                //     if (!params.data){
                                //         return params.value
                                //     }
    
                                //     return <a className="data-grid-link" target='_blank' href={"/panel/items/view/" + params.data.item_id}><h3 className="title">{params.value}</h3></a>
                                // }
                                type: "quick-preview",
                                cellRendererParams: {
                                    appClass: () => new ItemsApp(),
                                    elemIdTitle: "item_id",
                                }
                            },
                            {
                                field: "request_ref",
                                headerName: "Request ref.:",
                                cellRenderer: (params: ICellRendererParams<IIssuesModal>) => {
                                    if (!params.data){
                                        return params.value
                                    }
    
                                    return <a className="data-grid-link" target='_blank' href={"/panel/item_requests/view/" + params.data.request_ref}><h3 className="title">{params.value}</h3></a>
                                }
                            },
                            {
                                field: "caused_by_name",
                                headerName: "Caused by",
                                width: 250,
                                type: "quick-preview",
                                cellRendererParams: {
                                    appClass: () => new UsersApp(),
                                    elemIdTitle: "caused_by",
                                }
                            },
                            {
                                field: "reported_by_title",
                                headerName: "Reported by",
                                width: 250,
                                type: "quick-preview",
                                cellRendererParams: {
                                    appClass: () => new UsersApp(),
                                    elemIdTitle: "reported_by",
                                }
                            },
                            {
                                field: "timestamp",
                                headerName: "Reported on",
                                type: "date",
                                width: 250,

                            },
                            {
                                field: "resolved_by_title",
                                headerName: "Resolved by",
                                width: 250,
                                type: "quick-preview",
                                cellRendererParams: {
                                    appClass: () => new UsersApp(),
                                    elemIdTitle: "resolved_by",
                                }
                            },
                            {
                                field: "resolved_at",
                                headerName: "Resolved on",
                                type: "date",
                                width: 250
                            },
    ]

    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/issues/view/[id]",
        editItemLink: "/panel/issues/edit/[id]",
        rowHeight: 50,
        disableDelete: true,
        disableSelect: true,
        actionButtons: {
            pinned: "right",
            width: 150
        },
        filterForm: {
            name: "issues-data-displayer-filter-form",
            elements: [
                {
                    id: "type",
                    xs: 6,
                    type: "Select",
                    label: "Type",
                    fullWidth: true,
                    displayForItem: true,
                    addNoneItem: true,
                    items: issuesTypesList
                },
                {
                    id: "item_id",
                    xs: 6,
                    type: "DynamicModelSelect",
                    label: "Item",
                    fullWidth: true,
                    appClass: new ItemsApp(),
                    selectLabel: "Select Item",
                    gridColumns: new ItemsApp().gridDataColumns
                },
                {
                    id: "caused_by",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Caused by",
                    fullWidth: true,
                    appClass: new UsersApp(),
                    selectLabel: "Select User",
                    gridColumns: new UsersApp().gridDataColumns
                },
                {
                    id: "resolved_by",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Resolved by",
                    fullWidth: true,
                    appClass: new UsersApp(),
                    selectLabel: "Select User",
                    gridColumns: new UsersApp().gridDataColumns
                },
                {
                    id: "reported_by",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Reported by",
                    fullWidth: true,
                    appClass: new UsersApp(),
                    selectLabel: "Select User",
                    gridColumns: new UsersApp().gridDataColumns
                },
            ]
        }
    }

}